import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { api } from '@/api/client';

interface FormData {
  name: string;
  code?: string;
  contactName?: string;
  contactPhone?: string;
  city?: string;
  stateId?: number | '';
  pincode?: string;
  address?: string;
  rtoMobile?: string;
  rtoCity?: string;
  rtoStateId?: number | '';
  rtoPincode?: string;
  rtoAddress?: string;
  isActive?: boolean;
}

interface State { id: number; name: string }

interface Props {
  warehouseId?: number;
  defaultValues?: Partial<FormData>;
  onSuccess: () => void;
}

export function MasterWarehouseForm({ warehouseId, defaultValues, onSuccess }: Props) {
  const qc = useQueryClient();
  const isEdit = !!warehouseId;
  const [sameAsBasic, setSameAsBasic] = useState(false);

  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get<State[]>('/states')).data,
    staleTime: 1000 * 60 * 60,
  });

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: { isActive: true, ...defaultValues },
  });

  const basicCity = watch('city');
  const basicStateId = watch('stateId');
  const basicPincode = watch('pincode');
  const basicAddress = watch('address');
  const basicMobile = watch('contactPhone');

  useEffect(() => {
    if (sameAsBasic) {
      setValue('rtoCity', basicCity ?? '');
      setValue('rtoStateId', basicStateId ?? '');
      setValue('rtoPincode', basicPincode ?? '');
      setValue('rtoAddress', basicAddress ?? '');
      setValue('rtoMobile', basicMobile ?? '');
    }
  }, [sameAsBasic, basicCity, basicStateId, basicPincode, basicAddress, basicMobile, setValue]);

  const save = useMutation({
    mutationFn: async (d: FormData) => {
      const payload: any = { ...d };
      if (payload.stateId === '' || payload.stateId == null) delete payload.stateId; else payload.stateId = Number(payload.stateId);
      if (payload.rtoStateId === '' || payload.rtoStateId == null) delete payload.rtoStateId; else payload.rtoStateId = Number(payload.rtoStateId);
      if (payload.stateId && states) payload.state = states.find(s => s.id === payload.stateId)?.name;
      if (payload.rtoStateId && states) payload.rtoState = states.find(s => s.id === payload.rtoStateId)?.name;
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (isEdit) return (await api.patch(`/master-warehouses/${warehouseId}`, payload)).data;
      return (await api.post('/master-warehouses', payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['master-warehouses'] }); toast.success(isEdit ? 'Updated' : 'Created'); onSuccess(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save'),
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5">
      <Section title="Basic Information">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Warehouse Name *" error={errors.name?.message}>
            <input {...register('name', { required: 'Required' })} placeholder="Mumbai Master Hub" className="input" />
          </Field>
          <Field label="Code">
            <input {...register('code')} placeholder="MUM-MASTER-01" className="input" />
          </Field>
          <Field label="Contact Person">
            <input {...register('contactName')} className="input" />
          </Field>

          <Field label="Mobile">
            <input {...register('contactPhone')} placeholder="10-digit" className="input" />
          </Field>
          <Field label="City">
            <input {...register('city')} className="input" />
          </Field>
          <Field label="State">
            <select {...register('stateId')} className="input">
              <option value="">— Select state —</option>
              {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Pincode">
            <input {...register('pincode', { pattern: { value: /^\d{0,6}$/, message: '6 digits' } })} className="input font-mono" />
          </Field>
          <div className="col-span-2">
            <Field label="Address">
              <input {...register('address')} className="input" />
            </Field>
          </div>
        </div>
      </Section>

      <Section title="RTO Information" right={
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={sameAsBasic} onChange={(e) => setSameAsBasic(e.target.checked)} className="rounded" />
          Same as Basic Information
        </label>
      }>
        <div className="grid grid-cols-3 gap-3">
          <Field label="RTO Mobile">
            <input {...register('rtoMobile')} disabled={sameAsBasic} className="input disabled:bg-slate-50" />
          </Field>
          <Field label="RTO City">
            <input {...register('rtoCity')} disabled={sameAsBasic} className="input disabled:bg-slate-50" />
          </Field>
          <Field label="RTO State">
            <select {...register('rtoStateId')} disabled={sameAsBasic} className="input disabled:bg-slate-50">
              <option value="">— Select state —</option>
              {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="RTO Pincode">
            <input {...register('rtoPincode')} disabled={sameAsBasic} className="input font-mono disabled:bg-slate-50" />
          </Field>
        </div>
        <Field label="RTO Address">
          <textarea {...register('rtoAddress')} disabled={sameAsBasic} rows={2} className="input disabled:bg-slate-50" />
        </Field>
      </Section>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" {...register('isActive')} className="rounded" />
          Active
        </label>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onSuccess} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={save.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
            {save.isPending && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Master Warehouse'}
          </button>
        </div>
      </div>
    </form>
  );
}

function Section({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
